import React from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import FilterConditionRow from "./FilterConditionRow";

const FilterGroup = ({
  group, depth = 0, allowRemove = false,
  fields, fieldMap,
  addConditionToGroup, addGroupToGroup,
  removeConditionById, updateGroupLogic, removeGroupById,
  updateConditionField, updateConditionOperator, updateConditionValue,
}) => {
  const { t } = useTranslation();
  const borderColor = depth === 0 ? "border-primary/40" : "border-border";
  const bgColor = depth === 0 ? "bg-muted/50" : "bg-muted/60";

  const sharedProps = {
    fields, fieldMap,
    addConditionToGroup, addGroupToGroup,
    removeConditionById, updateGroupLogic, removeGroupById,
    updateConditionField, updateConditionOperator, updateConditionValue,
  };

  return (
    <div className={`space-y-3 border ${borderColor} ${bgColor} rounded-lg p-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Label className="text-xs uppercase text-muted-foreground">{t("filterComp.condition")}</Label>
          <Select value={group.logic} onValueChange={(val) => updateGroupLogic(group.id, val)}>
            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="and">{t("filterComp.logicAnd")}</SelectItem>
              <SelectItem value="or">{t("filterComp.logicOr")}</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {t("filterComp.rulesMustMatch", {
              logic: group.logic === "and" ? t("filterComp.logicAnd") : t("filterComp.logicOr"),
            })}
          </span>
        </div>
        {allowRemove && (
          <Button variant="ghost" size="icon" onClick={() => removeGroupById(group.id)}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {group.conditions.map((condition) =>
          Array.isArray(condition.conditions) ? (
            <FilterGroup key={condition.id} group={condition} depth={depth + 1} allowRemove {...sharedProps} />
          ) : (
            <FilterConditionRow
              key={condition.id}
              condition={condition}
              fields={fields}
              fieldMap={fieldMap}
              updateConditionField={updateConditionField}
              updateConditionOperator={updateConditionOperator}
              updateConditionValue={updateConditionValue}
              removeConditionById={removeConditionById}
            />
          )
        )}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => addConditionToGroup(group.id)}>
            <Plus className="w-4 h-4 mr-2" />
            {t("filterComp.addRule")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => addGroupToGroup(group.id)}>
            <Plus className="w-4 h-4 mr-2" />
            {t("filterComp.addGroup")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterGroup;
